import {
  RefCallback,
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState
} from 'react'

import clsx from 'clsx'
import { getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { HugeiconsIcon } from '@hugeicons/react'
import { PlusSignIcon } from '@hugeicons/core-solid-rounded'
import { useLingui } from '@lingui/react/macro'

import { GoodieFile, isGraphqlFile, isNewFile } from '../types'
import CropperDialog from '@/components/ui/CropperDialog'
import { Preview } from './FilePreview'
import { showToast, ToastType } from '@/features/toast'
import { resizeImage, ResizeOptions } from '../utils/resize'
import Loading from '@/components/ui/Loading'

const MAX_FILE_SIZE = 1024 * 1024 * 1024

type FileUploadProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange'
> & {
  value?: GoodieFile | null
  objectFit?: 'contain' | 'cover'
  onChange: (file: GoodieFile) => void
  onRemove?: () => void
  allowCaptions?: boolean
  canReplace?: boolean
  invalid?: boolean
  locale?: GraphQL.AppLocale
  isDefaultLocale?: boolean
  backgroundColor?: string
  crop?: {
    aspectRatio: number
    allowPadding?: boolean
    shape: 'round' | 'rect'
  }
  imageResize?: ResizeOptions
  ref?:
    | RefObject<HTMLInputElement | null>
    | RefCallback<HTMLInputElement | null>
}

function FileUpload(props: FileUploadProps) {
  const {
    className,
    placeholder,
    value,
    objectFit = 'cover',
    onChange,
    onRemove,
    allowCaptions = true,
    readOnly = false,
    canReplace = true,
    invalid = false,
    isDefaultLocale = false,
    backgroundColor,
    locale,
    crop,
    imageResize,
    ref: forwardedRef,
    ...rest
  } = props

  const [isLoading, setIsLoading] = useState(false)
  const internalRef = useRef<HTMLInputElement>(null)

  const [fileToCrop, setFileToCrop] = useState<File | null>(null)
  const { t } = useLingui()

  const setRef = useCallback(
    (node: HTMLInputElement | null) => {
      internalRef.current = node

      if (!forwardedRef) return

      if (typeof forwardedRef === 'function') {
        forwardedRef(node)
      } else {
        forwardedRef.current = node
      }
    },
    [forwardedRef]
  )

  const onChangeWithFile = useCallback(
    (newValue: GoodieFile) => {
      if (!isNewFile(newValue)) return
      if (!newValue.file.type.startsWith('image')) return onChange(newValue)
      if (!imageResize) return onChange(newValue)

      setIsLoading(true)

      resizeImage(newValue.file, imageResize)
        .then(file => onChange({ ...newValue, file }))
        .finally(() => setIsLoading(false))
    },
    [imageResize, setIsLoading, onChange]
  )

  const onChangeValue = useCallback(
    (value: GoodieFile) => {
      if (isNewFile(value)) onChange(value)
      else onChange({ ...value, isDirty: true })
    },
    [onChange]
  )

  const onFileUpdate = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]

      // This is needed so that users can also select the same file again (for example wants to crop it differently)
      // else it would just not call onChange anymore, because it things nothing changed
      event.target.value = ''

      if (!file) return

      if (file.size > MAX_FILE_SIZE) {
        showToast({
          id: 'file-too-big',
          type: ToastType.ERROR,
          message: t`File size exceeds 1GB limit. Please select a smaller file or reduce the video quality.`
        })

        return
      }

      const canCrop =
        file.type.startsWith('image/') || file.type.startsWith('video/')

      // If we are supposed to crop the file we give it to the CropperDialog
      if (crop && canCrop) {
        setFileToCrop(file)

        return
      }

      onChangeWithFile({
        ...value,
        file
      })
    },
    [crop, setFileToCrop, onChangeWithFile, value, t]
  )

  const onCropDone = useCallback(
    (file: File, metadata?: GraphQL.ProcessingMetadata) => {
      onChangeWithFile({
        file,
        processingMetadata: metadata
      })

      setFileToCrop(null)
    },
    [onChangeWithFile, setFileToCrop]
  )

  const onCropCancel = useCallback(() => {
    setFileToCrop(null)
  }, [setFileToCrop])

  const onCaptionsUpdate = useCallback(
    (captions: GraphQL.Caption[]) => {
      if (!value) return

      onChangeValue({ ...value, captions })
    },
    [onChangeValue, value]
  )

  const onToggleAlwaysMute = useCallback(() => {
    if (!value) return

    onChangeValue({ ...value, alwaysMute: !value?.alwaysMute })
  }, [onChangeValue, value])

  const translations = useMemo(() => {
    if (!value) return

    if (isGraphqlFile(value)) {
      return value.translations?.map(trans =>
        getFragment(SharedAPI.translation.TRANSLATION_FRAGMENT, trans)
      )
    }

    return value.translations
  }, [value])

  const altText = useMemo(() => {
    if (isDefaultLocale) return value?.altText ?? ''

    return (
      translations?.find(t => t?.locale === locale && t.field === 'alt_text')
        ?.value ?? ''
    )
  }, [value, translations, locale, isDefaultLocale])

  const onChangeAltText = useCallback(
    (
      altText: string | null,
      altTextTranslations?: (GraphQL.TranslationInput | GraphQL.Translation)[]
    ) => {
      if (!value) return

      const newTrans = [
        ...(translations?.filter(t => t.field !== 'alt_text') ?? []),
        ...(altTextTranslations ?? [])
      ]

      if (isNewFile(value)) {
        onChangeValue({
          ...value,
          altText: altText ?? undefined,
          translations: newTrans as GraphQL.TranslationInput[]
        })
      } else {
        onChangeValue({
          ...value,
          isDirty: true,
          altText: altText ?? undefined,
          translations: newTrans
        })
      }
    },
    [onChangeValue, translations, value]
  )

  return (
    <label
      className={clsx(
        'relative flex aspect-2/3 w-44 cursor-pointer flex-col items-center justify-center gap-2 overflow-hidden rounded-small bg-white text-text-light outline-primary',
        !value && 'border border-text-light p-1 focus-within:outline-solid',
        crop?.shape === 'round' && 'rounded-full',
        invalid && 'border-red-500',
        className
      )}
      style={{
        backgroundColor: backgroundColor ?? 'transparent'
      }}
      onClick={
        value && !canReplace ? event => event?.preventDefault() : undefined
      }
    >
      {!value && !readOnly && (
        <>
          <HugeiconsIcon icon={PlusSignIcon} className="size-5" />
          {placeholder && (
            <span className="px-2 text-center text-small">{placeholder}</span>
          )}
        </>
      )}
      {value && (
        <Preview
          allowCaptions={allowCaptions}
          objectFit={objectFit}
          value={value}
          canReplace={canReplace}
          readOnly={readOnly}
          locale={locale}
          onEdit={() => internalRef?.current?.click()}
          onChangeCaptions={onCaptionsUpdate}
          onRemove={onRemove}
          onAlwaysMute={onToggleAlwaysMute}
          altText={altText}
          translations={translations}
          onChangeAltText={onChangeAltText}
        />
      )}
      <input
        readOnly={readOnly}
        disabled={readOnly}
        {...rest}
        ref={setRef}
        type="file"
        onChange={onFileUpdate}
        tabIndex={value ? -1 : 0}
        aria-invalid={invalid}
        className="absolute size-0 opacity-0"
      />
      {isLoading && (
        <div className="absolute inset-0 size-full bg-black/20 backdrop-blur-lg flex flex-row justify-center items-center">
          <Loading />
        </div>
      )}
      <CropperDialog
        onDone={onCropDone}
        onCancel={onCropCancel}
        file={fileToCrop}
        allowPadding={crop?.allowPadding}
        aspectRatio={crop?.aspectRatio}
        shape={crop?.shape}
        backgroundColor={backgroundColor}
      />
    </label>
  )
}

export default FileUpload
